import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useBlocksDueReview,
  useCycles,
  useDeleteCycle,
  useExerciseConfig,
  useGameModels,
  useMicrocycles,
  useOrg,
  usePhases,
  useSaveCycle,
  useSeasons,
  useTeams,
  type CycleInputBody,
  type CycleRow,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, formatDateRange } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import { cycleReport, type BuildContext } from '@/reports/build';
import { ReportView } from '@/reports/ReportView';
import { exportReportPdf, reportFilename } from '@/reports/export';
import { addDays, todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { PaperPreview } from '@/ui/PaperPreview';
import { Section } from '@/ui/Section';
import { Chip } from '@/ui/Chip';

const DIMENSIONS = ['team', 'technical', 'tactical', 'physical', 'mental', 'social'] as const;

/** The questions a block has to answer, in the order the argument runs. */
const QUESTIONS = [
  'diagnosis',
  'evidence',
  'rationale',
  'successCriteria',
  'evaluationMethod',
  'modifyIf',
] as const;

type Draft = CycleInputBody & { objectives: { dimension: string; title: string; detail?: string }[] };

function blankDraft(): Draft {
  return {
    name: '',
    startsOn: todayISO(),
    endsOn: addDays(todayISO(), 27),
    objectives: DIMENSIONS.map((d) => ({ dimension: d, title: '' })),
    principleIds: [],
    physicalEmphasis: [],
  };
}

/**
 * Blocks.
 *
 * The form is the point. A block is not a list of what will be trained in March;
 * it is a statement of how March will be decided, and the app asks for that
 * statement rather than accepting a name and two dates. The last question —
 * what would make us modify this block — is the one that keeps the rest alive.
 */
export function Blocks({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const org = useOrg();
  const { user } = useAuthSession();

  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const seasons = useSeasons(activeTeamId ?? undefined);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const activeSeasonId =
    seasonId ??
    (seasons.data ?? []).find((s) => s.status === 'active')?.id ??
    (seasons.data ?? [])[0]?.id ??
    null;

  const cycles = useCycles(activeSeasonId);
  // Phases and blocks are different things that often cover the same weeks, so
  // the block says which phase it sits in rather than leaving the coach to
  // hold two calendars in their head.
  const phases = usePhases(activeSeasonId ?? '');
  const review = useBlocksDueReview();
  const save = useSaveCycle();
  const remove = useDeleteCycle();
  const models = useGameModels(activeTeamId ?? undefined);
  const exerciseConfig = useExerciseConfig();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editing, setEditing] = useState<
    { kind: 'macrocycle' | 'mesocycle'; id?: string; parentId?: string } | null
  >(null);
  const [draft, setDraft] = useState<Draft>(blankDraft());
  const [printing, setPrinting] = useState<{ cycle: CycleRow; kind: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const sheet = useRef<HTMLDivElement>(null);

  const macros = cycles.data ?? [];
  const phaseAt = (onDate: string) =>
    (phases.data ?? []).find((ph) => ph.startsOn <= onDate && onDate <= ph.endsOn);
  const principles = useMemo(
    () => (models.data ?? []).flatMap((m) => m.principles),
    [models.data],
  );
  const qualities = exerciseConfig.data?.qualities ?? [];
  const systems = exerciseConfig.data?.systems ?? [];

  const weeks = useMicrocycles(
    activeTeamId ?? undefined,
    printing?.cycle.startsOn,
    printing?.cycle.endsOn,
  );

  const set = <K extends keyof Draft>(k: K) => (value: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: value }));

  const startCreate = (kind: 'macrocycle' | 'mesocycle', parentId?: string) => {
    setEditing({ kind, parentId });
    setDraft(blankDraft());
  };

  const startEdit = (kind: 'macrocycle' | 'mesocycle', cycle: CycleRow, parentId?: string) => {
    setEditing({ kind, id: cycle.id, parentId });
    setDraft({
      name: cycle.name,
      startsOn: cycle.startsOn,
      endsOn: cycle.endsOn,
      dominantTheme: cycle.dominantTheme ?? undefined,
      secondaryTheme: cycle.secondaryTheme ?? undefined,
      diagnosis: cycle.diagnosis ?? undefined,
      evidence: cycle.evidence ?? undefined,
      rationale: cycle.rationale ?? undefined,
      principleIds: cycle.principleIds,
      physicalEmphasis: cycle.physicalEmphasis,
      successCriteria: cycle.successCriteria ?? undefined,
      evaluationMethod: cycle.evaluationMethod ?? undefined,
      modifyIf: cycle.modifyIf ?? undefined,
      reviewOn: cycle.reviewOn ?? undefined,
      outcome: cycle.outcome ?? undefined,
      sort: cycle.sort,
      objectives: DIMENSIONS.map((d) => {
        const found = cycle.objectives.find((o) => (o.dimension ?? 'team') === d);
        return { dimension: d, title: found?.title ?? '', detail: found?.detail ?? undefined };
      }),
    });
  };

  // How much of the statement is still missing. Shown rather than enforced: a
  // coach part-way through a block plan has not done something wrong.
  const unanswered = useMemo(
    () => QUESTIONS.filter((q) => !(draft[q] ?? '').toString().trim()).length,
    [draft],
  );

  const commit = async () => {
    if (!editing) return;
    const payload = {
      ...draft,
      // Blank objectives are not objectives; they are unanswered questions.
      objectives: (draft.objectives ?? []).filter((o) => o.title.trim()),
      ...(editing.kind === 'macrocycle'
        ? { seasonId: activeSeasonId ?? undefined }
        : { macrocycleId: editing.parentId }),
    };
    const ok = await submit(async () => {
      await save.mutateAsync({ kind: editing.kind, cycleId: editing.id, input: payload });
    });
    if (ok) setEditing(null);
  };

  const ctx: BuildContext = useMemo(
    () => ({
      locale,
      t: t as BuildContext['t'],
      meta: {
        clubName: org.data?.name ?? '',
        teamName: teamList.find((x) => x.id === activeTeamId)?.name,
        coachName: user?.name,
        seasonLabel: (seasons.data ?? []).find((s) => s.id === activeSeasonId)?.label,
        onDate: formatDate(todayISO(), locale),
        logoUrl: org.data?.logoKey
          ? `/api/org/logo?v=${encodeURIComponent(org.data.logoKey)}`
          : null,
        accent: org.data?.primaryColor,
        footer: org.data?.reportFooter,
      },
    }),
    [locale, t, org.data, teamList, activeTeamId, seasons.data, activeSeasonId, user?.name],
  );

  const report = useMemo(() => {
    if (!printing) return null;
    return cycleReport(ctx, printing.cycle, {
      kindLabel: printing.kind,
      principles,
      qualities: [...qualities, ...systems],
      weeks: (weeks.data ?? []).map((w) => ({
        name: w.name,
        startsOn: w.startsOn,
        endsOn: w.endsOn,
        objectiveSummary: w.objectiveSummary ?? null,
      })),
    });
  }, [printing, ctx, principles, qualities, systems, weeks.data]);

  const runExport = async () => {
    if (!sheet.current || !report) return;
    setExporting(true);
    setExportError(null);
    try {
      await exportReportPdf(
        sheet.current,
        reportFilename([report.meta.title], printing?.cycle.startsOn ?? todayISO()),
      );
    } catch {
      setExportError(t('report.failed'));
    } finally {
      setExporting(false);
    }
  };

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0 || (seasons.data ?? []).length === 0) {
    return (
      <>
        <PageHeader
        title={t('cycle.title')}
        lead={t('cycle.lead')}
        help={localePath(locale, '/guide/plan')}
      />
        <EmptyState title={t('development.noTeam')} body={t('development.noTeamBody')} />
      </>
    );
  }

  const due = review.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('cycle.title')}
        lead={t('cycle.lead')}
        help={localePath(locale, '/guide/plan')}
      />

      <div className="mb-4 flex flex-wrap gap-x-5 gap-y-2">
        {teamList.length > 1 ? (
          <div className="w-48">
            <Field label={t('nav.team')}>
              {(p) => (
                <SelectInput
                  {...p}
                  value={activeTeamId ?? ''}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  {teamList.map((tm) => (
                    <option key={tm.id} value={tm.id}>
                      {tm.name}
                    </option>
                  ))}
                </SelectInput>
              )}
            </Field>
          </div>
        ) : null}
        <div className="w-48">
          <Field label={t('seasons.title')}>
            {(p) => (
              <SelectInput
                {...p}
                value={activeSeasonId ?? ''}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                {(seasons.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>
        </div>
      </div>

      {due.length > 0 ? (
        <div className="mb-4">
          <Panel title={t('cycle.dueReview')} count={due.length}>
            <p className="px-4 pt-3 text-meta text-ink-muted">{t('cycle.dueReviewBody')}</p>
            <ul className="divide-y divide-line">
              {due.map((b) => (
                <li key={b.id} className="px-4 py-3">
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-medium">{b.name}</span>
                    <span className="tabular text-meta text-ink-faint">
                      {b.reviewOn ? formatDate(b.reviewOn, locale) : ''}
                    </span>
                  </span>
                  {b.modifyIf ? (
                    <p className="mt-1 max-w-[80ch] text-meta text-ink-muted">{b.modifyIf}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      ) : null}

      {editing ? (
        <div className="mb-5">
          <Panel
            title={editing.id ? t('cycle.edit') : t('cycle.title')}
            count={
              unanswered > 0
                ? t('cycle.incomplete', { count: unanswered })
                : t('cycle.complete')
            }
            action={
              <Button variant="quiet" onClick={() => setEditing(null)}>
                {t('common.cancel')}
              </Button>
            }
          >
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void commit();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Section title={t('cycle.name')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-4">
                  <div className="sm:col-span-2">
                    <Field label={t('cycle.name')} error={fieldError('name')}>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={draft.name}
                          onChange={(e) => set('name')(e.target.value)}
                          required
                          autoFocus
                        />
                      )}
                    </Field>
                  </div>
                  <Field label={t('register.from')} error={fieldError('startsOn')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={draft.startsOn}
                        onChange={(e) => set('startsOn')(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                  <Field label={t('register.to')} error={fieldError('endsOn')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={draft.endsOn}
                        onChange={(e) => set('endsOn')(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section title={t('cycle.diagnosis')} hint={t('cycle.diagnosisHint')}>
                <div className="grid gap-x-5 gap-y-2">
                  <Field label={t('cycle.diagnosis')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={draft.diagnosis ?? ''}
                        onChange={(e) => set('diagnosis')(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('cycle.evidence')} hint={t('cycle.evidenceHint')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={draft.evidence ?? ''}
                        onChange={(e) => set('evidence')(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('cycle.rationale')} hint={t('cycle.rationaleHint')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={draft.rationale ?? ''}
                        onChange={(e) => set('rationale')(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section title={t('cycle.objectives')} hint={t('cycle.objectivesHint')}>
                <div className="grid gap-x-5 gap-y-2">
                  {DIMENSIONS.map((dimension, i) => (
                    <Field
                      key={dimension}
                      label={
                        dimension === 'team'
                          ? t('cycle.teamObjective')
                          : t(`development.dimension.${dimension}`)
                      }
                      optional={dimension !== 'team'}
                    >
                      {(p) => (
                        <TextInput
                          {...p}
                          value={draft.objectives?.[i]?.title ?? ''}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              objectives: (d.objectives ?? []).map((o, j) =>
                                j === i ? { ...o, title: e.target.value } : o,
                              ),
                            }))
                          }
                        />
                      )}
                    </Field>
                  ))}
                </div>
              </Section>

              <Section title={t('cycle.emphasis')}>
                <div className="grid gap-4">
                  <div>
                    <p className="mb-2 text-meta text-ink-muted">{t('cycle.principlesHint')}</p>
                    {principles.length === 0 ? (
                      <p className="text-meta text-ink-faint">{t('gameModel.noPrinciples')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {principles.map((principle) => {
                          const on = (draft.principleIds ?? []).includes(principle.id);
                          return (
                            <button
                              key={principle.id}
                              type="button"
                              aria-pressed={on}
                              onClick={() =>
                                set('principleIds')(
                                  on
                                    ? (draft.principleIds ?? []).filter((x) => x !== principle.id)
                                    : [...(draft.principleIds ?? []), principle.id],
                                )
                              }
                              className={`rounded-(--radius-control) border px-2.5 py-1 text-meta transition-colors ${
                                on
                                  ? 'border-ink bg-ink text-paper'
                                  : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                              }`}
                            >
                              {principle.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="mb-2 text-meta text-ink-muted">{t('cycle.physical')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[...qualities, ...systems].map((q) => {
                        const on = (draft.physicalEmphasis ?? []).includes(q.key);
                        return (
                          <button
                            key={q.key}
                            type="button"
                            aria-pressed={on}
                            onClick={() =>
                              set('physicalEmphasis')(
                                on
                                  ? (draft.physicalEmphasis ?? []).filter((x) => x !== q.key)
                                  : [...(draft.physicalEmphasis ?? []), q.key],
                              )
                            }
                            className={`rounded-(--radius-control) border px-2.5 py-1 text-meta transition-colors ${
                              on
                                ? 'border-ink bg-ink text-paper'
                                : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                            }`}
                          >
                            {configLabel(q, t)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Section>

              <Section title={t('cycle.successCriteria')} hint={t('cycle.successHint')}>
                <div className="grid gap-x-5 gap-y-2">
                  <Field label={t('cycle.successCriteria')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={draft.successCriteria ?? ''}
                        onChange={(e) => set('successCriteria')(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('cycle.evaluationMethod')} hint={t('cycle.evaluationHint')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={2}
                        value={draft.evaluationMethod ?? ''}
                        onChange={(e) => set('evaluationMethod')(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              {/* Last, and on its own, because it is the field that turns the
                  rest from a prediction into a plan. */}
              <Section title={t('cycle.modifyIf')} hint={t('cycle.modifyIfHint')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-[1fr_12rem]">
                  <Field label={t('cycle.modifyIf')}>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={3}
                        placeholder={t('cycle.modifyIfExample')}
                        value={draft.modifyIf ?? ''}
                        onChange={(e) => set('modifyIf')(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('cycle.reviewOn')} optional hint={t('cycle.reviewOnHint')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={draft.reviewOn ?? ''}
                        onChange={(e) => set('reviewOn')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t('cycle.outcome')} optional hint={t('cycle.outcomeHint')}>
                      {(p) => (
                        <TextArea
                          {...p}
                          rows={2}
                          value={draft.outcome ?? ''}
                          onChange={(e) => set('outcome')(e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </Section>

              <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy} disabled={!draft.name.trim()}>
                  {t('cycle.save')}
                </Button>
                {editing.id ? (
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!window.confirm(t('cycle.confirmDelete'))) return;
                      void remove
                        .mutateAsync({ kind: editing.kind, cycleId: editing.id! })
                        .then(() => setEditing(null));
                    }}
                  >
                    {t('cycle.delete')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel
        count={macros.length}
        action={
          editing ? null : (
            <Button variant="primary" onClick={() => startCreate('macrocycle')}>
              {t('report.macrocycle')}
            </Button>
          )
        }
      >
        {cycles.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : macros.length === 0 ? (
          <EmptyState
            title={t('plan.empty')}
            body={t('cycle.lead')}
            action={
              <Button variant="primary" onClick={() => startCreate('macrocycle')}>
                {t('report.macrocycle')}
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {macros.map((macro) => (
              <li key={macro.id} className="px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-condensed text-subhead font-semibold">{macro.name}</span>
                  <span className="tabular text-meta text-ink-faint">
                    {formatDateRange(macro.startsOn, macro.endsOn, locale)}
                  </span>
                  {phaseAt(macro.startsOn) ? (
                    <Chip muted>{phaseAt(macro.startsOn)!.name}</Chip>
                  ) : null}
                  <span className="ms-auto flex flex-wrap gap-2">
                    <Button
                      variant="quiet"
                      onClick={() => setPrinting({ cycle: macro, kind: t('report.macrocycle') })}
                    >
                      {t('cycle.print')}
                    </Button>
                    <Button variant="quiet" onClick={() => startEdit('macrocycle', macro)}>
                      {t('common.edit')}
                    </Button>
                    <Button variant="secondary" onClick={() => startCreate('mesocycle', macro.id)}>
                      {t('report.mesocycle')}
                    </Button>
                  </span>
                </div>

                <ul className="mt-2 grid gap-2">
                  {macro.mesocycles.map((meso) => (
                    <li
                      key={meso.id}
                      className="rounded-(--radius-control) border border-line bg-surface-sunken px-3 py-2"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-medium">{meso.name}</span>
                        <span className="tabular text-meta text-ink-faint">
                          {formatDateRange(meso.startsOn, meso.endsOn, locale)}
                        </span>
                        {meso.reviewOn ? (
                          <Chip muted>
                            {t('cycle.reviewOn')} {formatDate(meso.reviewOn, locale)}
                          </Chip>
                        ) : null}
                        <span className="ms-auto flex gap-2">
                          <Button
                            variant="quiet"
                            onClick={() =>
                              setPrinting({ cycle: meso, kind: t('report.mesocycle') })
                            }
                          >
                            {t('cycle.print')}
                          </Button>
                          <Button
                            variant="quiet"
                            onClick={() => startEdit('mesocycle', meso, macro.id)}
                          >
                            {t('common.edit')}
                          </Button>
                        </span>
                      </div>
                      {meso.objectives.length > 0 ? (
                        <p className="mt-1 max-w-[80ch] text-meta text-ink-muted">
                          {meso.objectives[0]!.title}
                        </p>
                      ) : null}
                      {meso.modifyIf ? (
                        <p className="mt-1 max-w-[80ch] text-meta text-signal">
                          {t('cycle.modifyIf')} {meso.modifyIf}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {report ? (
        <div className="mt-5 grid gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => window.print()}>
              {t('report.print')}
            </Button>
            <Button variant="primary" busy={exporting} onClick={() => void runExport()}>
              {exporting ? t('report.preparing') : t('report.export')}
            </Button>
            <Button variant="quiet" onClick={() => setPrinting(null)}>
              {t('common.close')}
            </Button>
            {exportError ? <Notice tone="risk">{exportError}</Notice> : null}
          </div>
          {/* The block plan on the club's paper, whole rather than a third of it. The
              sheet itself is untouched — only what is drawn is scaled. */}
          <PaperPreview className="report-preview">
            <div ref={sheet} className="mx-auto w-fit shadow-(--shadow-sheet)">
              <ReportView report={report} />
            </div>
          </PaperPreview>
        </div>
      ) : null}
    </div>
  );
}
